import { BUILDING_DEFINITIONS, BuildingType, UNIT_DEFINITIONS, UnitType, WeaponStatsMap } from '../shared/MessageType';

// Interfaces for instance data
interface UnitInstanceData {
    id: number;
    playerId: number;
    k: UnitType;
    x: number;
    y: number;
    life: number;
}

interface BuildingInstanceData {
    id: number;
    playerId: number;
    k: BuildingType;
    x: number;
    y: number;
    life: number;
}

export class UnitBuildingInfoPanel3D {
    private panelElement: HTMLElement | null = null;
    private detailPanelElement: HTMLElement | null = null;
    private unitsSection: HTMLElement | null = null;
    private buildingsSection: HTMLElement | null = null;
    private isVisible: boolean = false;
    private currentlySelectedId: number | null = null;
    private currentlySelectedType: 'unit' | 'building' | null = null;
    private isShowingInstance: boolean = false;

    constructor() {
        this.createPanel();
        this.setupToggleButton();
    }

    private createPanel() {
        // Create main panel container
        const panel = document.createElement('div');
        panel.id = 'unit-building-panel';
        panel.style.cssText = `
            position: fixed;
            right: 0;
            top: 5%;
            width: 200px;
            height: 95vh;
            background-color: rgba(10, 10, 10, 0.95);
            border-right: 2px solid #444;
            z-index: 1000;
            overflow-y: auto;
            font-family: Arial, sans-serif;
            color: #fff;
            display: none;
        `;

        // Title
        const title = document.createElement('div');
        title.textContent = 'Units & Buildings';
        title.style.cssText = `
            padding: 10px;
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            border-bottom: 1px solid #444;
        `;
        panel.appendChild(title);

        // Units section
        const unitsSection = document.createElement('div');
        unitsSection.id = 'units-section';
        unitsSection.style.cssText = 'padding: 10px;';

        const unitsTitle = document.createElement('div');
        unitsTitle.textContent = 'UNITS';
        unitsTitle.style.cssText = `
            font-size: 12px;
            color: #888;
            margin-bottom: 10px;
        `;
        unitsSection.appendChild(unitsTitle);

        const unitsGrid = document.createElement('div');
        unitsGrid.style.cssText = `
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 4px;
        `;

        const unitTypes = [
            UnitType.Worker,
            UnitType.Tank,
            UnitType.Artillery,
            UnitType.ScoutPlane
        ];

        unitTypes.forEach(unitType => {
            const icon = this.createIcon(unitType, 'unit', UNIT_DEFINITIONS[unitType].cost);
            icon.addEventListener('click', () => this.toggleUnitDetails(unitType));
            unitsGrid.appendChild(icon);
        });

        unitsSection.appendChild(unitsGrid);
        panel.appendChild(unitsSection);
        this.unitsSection = unitsSection;

        // Buildings section
        const buildingsSection = document.createElement('div');
        buildingsSection.id = 'buildings-section';
        buildingsSection.style.cssText = 'padding: 10px;';

        const buildingsTitle = document.createElement('div');
        buildingsTitle.textContent = 'BUILDINGS';
        buildingsTitle.style.cssText = `
            font-size: 12px;
            color: #888;
            margin-bottom: 10px;
            margin-top: 20px;
        `;
        buildingsSection.appendChild(buildingsTitle);

        const buildingsGrid = document.createElement('div');
        buildingsGrid.style.cssText = `
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 4px;
        `;

        const buildingTypes = [
            BuildingType.Cannon,
            BuildingType.LaserCannon,
            BuildingType.MissileCannon,
            BuildingType.OreProcessor,
            BuildingType.OrbitalMassDriver,
            BuildingType.Artillery
        ];

        buildingTypes.forEach(buildingType => {
            const icon = this.createIcon(buildingType, 'building', BUILDING_DEFINITIONS[buildingType].cost);
            icon.addEventListener('click', () => this.toggleBuildingDetails(buildingType));
            buildingsGrid.appendChild(icon);
        });

        buildingsSection.appendChild(buildingsGrid);
        panel.appendChild(buildingsSection);
        this.buildingsSection = buildingsSection;

        // Detail section at the bottom
        const detailSection = document.createElement('div');
        detailSection.id = 'unit-building-detail-section';
        detailSection.style.cssText = `
            border-top: 2px solid #444;
            margin-top: 20px;
            display: none;
        `;

        // Title
        const detailTitle = document.createElement('div');
        detailTitle.id = 'detail-title';
        detailTitle.style.cssText = `
            padding: 10px;
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            border-bottom: 1px solid #444;
        `;
        detailSection.appendChild(detailTitle);

        // Details content
        const details = document.createElement('div');
        details.id = 'detail-content';
        details.style.cssText = `
            padding: 10px;
            font-size: 12px;
            color: #ccc;
            line-height: 1.6;
            white-space: pre-wrap;
        `;
        detailSection.appendChild(details);

        panel.appendChild(detailSection);
        this.detailPanelElement = detailSection;

        document.body.appendChild(panel);
        this.panelElement = panel;
    }

    private createIcon(id: number | string, type: 'unit' | 'building', cost: number): HTMLElement {
        const icon = document.createElement('div');
        icon.style.cssText = `
            width: 48px;
            height: 48px;
            background-color: #222;
            border: 1px solid #555;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            position: relative;
            transition: background-color 0.2s, border-color 0.2s;
        `;

        const name = type === 'unit'
            ? this.getUnitTypeName(id as UnitType)
            : this.getBuildingTypeName(id as BuildingType)

        const nameText = document.createElement('div');
        nameText.textContent = name;
        nameText.style.cssText = `
            font-size: 10px;
            color: #fff;
            text-align: center;
        `;
        icon.appendChild(nameText);

        const costText = document.createElement('div');
        costText.textContent = cost.toString();
        costText.style.cssText = `
            font-size: 8px;
            color: #ffd700;
            position: absolute;
            bottom: 2px;
            text-align: center;
        `;
        icon.appendChild(costText);

        // Hover effects
        icon.addEventListener('mouseenter', () => {
            icon.style.backgroundColor = '#444';
            icon.style.borderColor = '#888';
        });

        icon.addEventListener('mouseleave', () => {
            icon.style.backgroundColor = '#222';
            icon.style.borderColor = '#555';
        });

        return icon;
    }


    private toggleUnitDetails(unitType: UnitType) {
        // If clicking the same unit, hide details
        if (this.currentlySelectedType === 'unit' && this.currentlySelectedId === unitType) {
            this.hideDetails();
            this.showTypeButtons();
            this.isShowingInstance = false;
            return;
        }

        // Show type buttons when viewing type details (not instance)
        this.showTypeButtons();
        this.isShowingInstance = false;

        // Otherwise show the new unit details
        this.showUnitDetails(unitType);
        this.currentlySelectedType = 'unit';
        this.currentlySelectedId = unitType;
    }

    private showUnitDetails(unitType: UnitType) {
        if (!this.detailPanelElement) return;

        const stats = UNIT_DEFINITIONS[unitType];
        const weaponType = stats.defaultWeapon;
        const weaponStats = weaponType ? WeaponStatsMap[weaponType] : null;
        const sightRange = stats.sightRange;

        const title = document.getElementById('detail-title');
        const details = document.getElementById('detail-content');

        if (title) {
            title.textContent = this.getUnitTypeName(unitType);
        }

        if (details) {
            let detailsText = `Cost: ${stats.cost} Metal\n`;
            detailsText += `Health: ${stats.life} HP\n`;
            detailsText += `Vision Range: ${sightRange}\n\n`;

            if (weaponStats) {
                detailsText += `WEAPON:\n`;
                detailsText += `Damage: ${weaponStats.damage}\n`;
                detailsText += `Range: ${weaponStats.range}\n`;
                detailsText += `Cooldown: ${weaponStats.cooldown}s\n`;
                if (weaponStats.shotCount > 1) {
                    detailsText += `Shots: ${weaponStats.shotCount}\n`;
                }
                detailsText += `\n`;
            }

            detailsText += `REQUIREMENTS:\n`;
            detailsText += `• Requires Hub to build\n`;

            details.textContent = detailsText;
        }

        if (this.detailPanelElement) {
            this.detailPanelElement.style.display = 'block';
        }
    }

    private toggleBuildingDetails(buildingType: BuildingType) {
        // If clicking the same building, hide details
        if (this.currentlySelectedType === 'building' && this.currentlySelectedId === buildingType) {
            this.hideDetails();
            this.showTypeButtons();
            this.isShowingInstance = false;
            return;
        }

        // Show type buttons when viewing type details (not instance)
        this.showTypeButtons();
        this.isShowingInstance = false;

        // Otherwise show the new building details
        this.showBuildingDetails(buildingType);
        this.currentlySelectedType = 'building';
        this.currentlySelectedId = buildingType;
    }

    private showBuildingDetails(buildingType: BuildingType) {
        if (!this.detailPanelElement) return;

        const def = BUILDING_DEFINITIONS[buildingType];
        const cost = def.cost;
        const life = def.life;
        const limit = def.typeLimit;
        const sightRange = def.sightRange;
        const weaponType = def.defaultWeapon;
        const weaponStats = weaponType ? WeaponStatsMap[weaponType] : null;

        const title = document.getElementById('detail-title');
        const details = document.getElementById('detail-content');

        if (title) {
            title.textContent = this.getBuildingTypeName(buildingType);
        }

        if (details) {
            let detailsText = `Cost: ${cost} Metal\n`;
            detailsText += `Health: ${life} HP\n`;
            detailsText += `Max Count: ${limit === 0 ? 'Unlimited' : limit}\n`;
            detailsText += `Vision Range: ${sightRange}\n\n`;

            if (weaponStats) {
                detailsText += `WEAPON:\n`;
                detailsText += `Damage: ${weaponStats.damage}\n`;
                detailsText += `Range: ${weaponStats.range}\n`;
                detailsText += `Cooldown: ${weaponStats.cooldown}s\n`;
                if (weaponStats.shotCount > 1) {
                    detailsText += `Shots: ${weaponStats.shotCount}\n`;
                }
                detailsText += `\n`;
            }

            detailsText += `REQUIREMENTS:\n`;
            if (buildingType === BuildingType.Hub) {
                detailsText += `• Starting building\n`;
            } else {
                detailsText += `• Requires Hub to build\n`;
                if (buildingType === BuildingType.ArtilleryFactory) {
                    detailsText += `• Requires LightFactory\n`;
                }
            }
            detailsText += `• Must be built on open ground\n`;
            detailsText += `• Minimum 10 units from other buildings\n`;

            details.textContent = detailsText;
        }

        if (this.detailPanelElement) {
            this.detailPanelElement.style.display = 'block';
        }
    }

    private hideDetails() {
        if (this.detailPanelElement) {
            this.detailPanelElement.style.display = 'none';
        }
        this.currentlySelectedType = null;
        this.currentlySelectedId = null;
        // Don't change button visibility here - let the caller decide
    }

    private setupToggleButton() {
        const toggleButton = document.getElementById('toggle-unit-building-menu');
        if (toggleButton) {
            toggleButton.addEventListener('click', () => {
                this.toggle();
                // Update button appearance
                if (this.isVisible) {
                    toggleButton.style.backgroundColor = '#0891b2';
                } else {
                    toggleButton.style.backgroundColor = '#f0f0f0';
                }
            });
        }
    }

    private getUnitTypeName(unitType: UnitType): string {
        const names: { [key in UnitType]: string } = {
            [UnitType.Worker]: 'Worker',
            [UnitType.Tank]: 'Tank',
            [UnitType.Archer]: 'Archer',
            [UnitType.Crawler]: 'Crawler',
            [UnitType.LightTank]: 'Light Tank',
            [UnitType.MediumTank]: 'Medium Tank',
            [UnitType.HeavyTank]: 'Heavy Tank',
            [UnitType.Artillery]: 'Artillery',
            [UnitType.ScoutPlane]: 'Scout Plane'
        };
        return names[unitType] || 'Unknown';
    }

    private getBuildingTypeName(buildingType: BuildingType): string {
        const names: { [key in BuildingType]: string } = {
            [BuildingType.Hub]: 'Hub',
            [BuildingType.House]: 'House',
            [BuildingType.Cannon]: 'Cannon',
            [BuildingType.LaserCannon]: 'Laser Cannon',
            [BuildingType.MissileCannon]: 'Missile Cannon',
            [BuildingType.OreProcessor]: 'Ore Processor',
            [BuildingType.OrbitalMassDriver]: 'Orbital Mass Driver',
            [BuildingType.Artillery]: 'Artillery',
            [BuildingType.LightFactory]: 'Light Factory',
            [BuildingType.SolarFarm]: 'Solar Farm',
            [BuildingType.EnergyStorage]: 'Energy Storage',
            [BuildingType.ArtilleryFactory]: 'Artillery Factory',
            [BuildingType.Wall]: 'Wall'
        };
        return names[buildingType] || 'Unknown';
    }

    public toggle() {
        this.isVisible = !this.isVisible;
        if (this.panelElement) {
            this.panelElement.style.display = this.isVisible ? 'block' : 'none';
        }
        if (!this.isVisible) {
            this.hideDetails();
        }
    }

    public destroy() {
        if (this.panelElement) {
            this.panelElement.remove();
        }
        // detailPanelElement is now part of panelElement, so no need to remove separately
    }

    /**
     * Show details for a specific unit instance (clicked in-game)
     */
    public showUnitInstance(unit: UnitInstanceData) {
        if (!this.detailPanelElement) return;

        const stats = UNIT_DEFINITIONS[unit.k];
        const weaponType = stats.defaultWeapon;
        const weaponStats = weaponType ? WeaponStatsMap[weaponType] : null;
        const sightRange = stats.sightRange;

        const title = document.getElementById('detail-title');
        const details = document.getElementById('detail-content');

        if (title) {
            title.textContent = `${this.getUnitTypeName(unit.k)} #${unit.id}`;
        }

        if (details) {
            // Instance-specific stats
            let detailsText = `INSTANCE:\n`;
            detailsText += `ID: ${unit.id}\n`;
            detailsText += `Player: ${unit.playerId}\n`;
            detailsText += `Health: ${unit.life}/${stats.life} HP\n`;
            detailsText += `Position: (${Math.round(unit.x)}, ${Math.round(unit.y)})\n\n`;

            // Type stats
            detailsText += `TYPE STATS:\n`;
            detailsText += `Cost: ${stats.cost} Metal\n`;
            detailsText += `Max Health: ${stats.life} HP\n`;
            detailsText += `Vision Range: ${sightRange}\n\n`;

            if (weaponStats) {
                detailsText += `WEAPON:\n`;
                detailsText += `Damage: ${weaponStats.damage}\n`;
                detailsText += `Range: ${weaponStats.range}\n`;
                detailsText += `Cooldown: ${weaponStats.cooldown}s\n`;
                if (weaponStats.shotCount > 1) {
                    detailsText += `Shots: ${weaponStats.shotCount}\n`;
                }
                detailsText += `\n`;
            }

            detailsText += `REQUIREMENTS:\n`;
            detailsText += `• Requires Hub to build\n`;

            details.textContent = detailsText;
        }

        if (this.detailPanelElement) {
            this.detailPanelElement.style.display = 'block';
        }

        // Hide type selection buttons when showing an instance
        this.hideTypeButtons();
        this.isShowingInstance = true;

        // Ensure panel is visible
        if (this.panelElement && !this.isVisible) {
            this.toggle();
        }
    }

    /**
     * Show details for a specific building instance (clicked in-game)
     */
    public showBuildingInstance(building: BuildingInstanceData) {
        if (!this.detailPanelElement) return;

        const def = BUILDING_DEFINITIONS[building.k];
        const cost = def.cost;
        const life = building.life;
        const limit = def.typeLimit;
        const sightRange = def.sightRange;
        const weaponType = def.defaultWeapon;
        const weaponStats = weaponType ? WeaponStatsMap[weaponType] : null;

        const title = document.getElementById('detail-title');
        const details = document.getElementById('detail-content');

        if (title) {
            title.textContent = `${this.getBuildingTypeName(building.k)} #${building.id}`;
        }

        if (details) {
            // Instance-specific stats
            let detailsText = `INSTANCE:\n`;
            detailsText += `ID: ${building.id}\n`;
            detailsText += `Player: ${building.playerId}\n`;
            detailsText += `Health: ${building.life} HP\n`;
            detailsText += `Position: (${Math.round(building.x)}, ${Math.round(building.y)})\n\n`;

            // Type stats
            detailsText += `TYPE STATS:\n`;
            detailsText += `Cost: ${cost} Metal\n`;
            detailsText += `Health: ${life} HP\n`;
            detailsText += `Max Count: ${limit === 0 ? 'Unlimited' : limit}\n`;
            detailsText += `Vision Range: ${sightRange}\n\n`;

            if (weaponStats) {
                detailsText += `WEAPON:\n`;
                detailsText += `Damage: ${weaponStats.damage}\n`;
                detailsText += `Range: ${weaponStats.range}\n`;
                detailsText += `Cooldown: ${weaponStats.cooldown}s\n`;
                if (weaponStats.shotCount > 1) {
                    detailsText += `Shots: ${weaponStats.shotCount}\n`;
                }
                detailsText += `\n`;
            }

            detailsText += `REQUIREMENTS:\n`;
            if (building.k === BuildingType.Hub) {
                detailsText += `• Starting building\n`;
            } else {
                detailsText += `• Requires Hub to build\n`;
                if (building.k === BuildingType.ArtilleryFactory) {
                    detailsText += `• Requires LightFactory\n`;
                }
            }
            detailsText += `• Must be built on open ground\n`;
            detailsText += `• Minimum 10 units from other buildings\n`;

            details.textContent = detailsText;
        }

        if (this.detailPanelElement) {
            this.detailPanelElement.style.display = 'block';
        }

        // Hide type selection buttons when showing an instance
        this.hideTypeButtons();
        this.isShowingInstance = true;

        // Ensure panel is visible
        if (this.panelElement && !this.isVisible) {
            this.toggle();
        }
    }

    /**
     * Clear instance selection (clicked on empty space)
     */
    public clearInstanceSelection() {
        // Don't hide details if user is viewing type info
        // Only clear if we were showing an instance
        if (this.isShowingInstance) {
            this.hideDetails();
            this.showTypeButtons();
            this.isShowingInstance = false;

            this.isVisible = false;
            if (this.panelElement) {
                this.panelElement.style.display = this.isVisible ? 'block' : 'none';
            }
        }
    }

    /**
     * Hide the type selection buttons (units and buildings grids)
     */
    private hideTypeButtons() {
        if (this.unitsSection) {
            this.unitsSection.style.display = 'none';
        }
        if (this.buildingsSection) {
            this.buildingsSection.style.display = 'none';
        }
    }

    /**
     * Show the type selection buttons (units and buildings grids)
     */
    private showTypeButtons() {
        if (this.unitsSection) {
            this.unitsSection.style.display = 'block';
        }
        if (this.buildingsSection) {
            this.buildingsSection.style.display = 'block';
        }
    }
}

